import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  FileText, Plus, Search, Filter, Clock, AlertTriangle,
  CheckCircle2, ChevronRight, X, CreditCard, Calendar
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { toast } from 'sonner';
import SidebarLayout from '../components/SidebarLayout';
import { useAuth } from '../context/AuthContext';
import ChangeHistoryButton from '../components/ChangeHistoryButton';

import API from '../utils/api';
const SCOPE_TYPES = ['Retainer', 'Project'];
const PAYMENT_MODES = ['Bank Transfer', 'UPI', 'Cash', 'Cheque', 'Other'];

const STATUS_COLORS = {
  'Pending': 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  'Partially Paid': 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  'Cleared': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  'Overdue': 'bg-rose-500/10 text-rose-400 border-rose-500/20',
};

function formatCurrency(n) {
  if (n == null) return '—';
  return '₹' + Number(n).toLocaleString('en-IN');
}

export default function InvoicesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPayment, setShowPayment] = useState(null);
  const [fClient, setFClient] = useState('');
  const [fStatus, setFStatus] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (fClient && fClient !== 'all') params.client_id = fClient;
      if (fStatus && fStatus !== 'all') params.status = fStatus;
      const [invRes, statsRes, clientRes] = await Promise.all([
        axios.get(`${API}/invoices`, { withCredentials: true, params }),
        axios.get(`${API}/invoices/stats`, { withCredentials: true }),
        axios.get(`${API}/clients`, { withCredentials: true }),
      ]);
      setInvoices(invRes.data);
      setStats(statsRes.data);
      setClients(clientRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [fClient, fStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/invoices/${id}`, { withCredentials: true });
      toast.success('Invoice deleted');
      setSelectedInvoice(null);
      fetchData();
    } catch (err) { toast.error('Failed to delete'); }
  };

  if (loading && !stats) {
    return <SidebarLayout title="Invoices" subtitle="Invoice Tracker"><div className="flex items-center justify-center py-20"><div className="text-white">Loading...</div></div></SidebarLayout>;
  }

  return (
    <SidebarLayout title="Invoices" subtitle="Invoice Tracker">
      {/* KPI Cards */}
      <div className="kpi-scroll md:grid-cols-4 gap-4 mb-6" data-testid="invoice-kpis">
        <div className="kpi-card border-l-4 border-purple-500/20">
          <span className="text-xs font-semibold tracking-widest uppercase text-slate-400">Total Invoiced</span>
          <div className="font-heading text-2xl font-black text-white mt-1">{formatCurrency(stats?.total_invoiced)}</div>
        </div>
        <div className="kpi-card border-l-4 border-emerald-500/20">
          <span className="text-xs font-semibold tracking-widest uppercase text-slate-400">Total Received</span>
          <div className="font-heading text-2xl font-black text-emerald-400 mt-1">{formatCurrency(stats?.total_received)}</div>
        </div>
        <div className="kpi-card border-l-4 border-amber-500/20">
          <span className="text-xs font-semibold tracking-widest uppercase text-slate-400">Outstanding</span>
          <div className="font-heading text-2xl font-black text-amber-400 mt-1">{formatCurrency(stats?.total_outstanding)}</div>
        </div>
        <div className="kpi-card border-l-4 border-rose-500/20">
          <span className="text-xs font-semibold tracking-widest uppercase text-slate-400">Overdue</span>
          <div className="font-heading text-2xl font-black text-rose-400 mt-1">{stats?.overdue_count || 0}</div>
        </div>
      </div>

      {/* Filters & Actions */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Select value={fClient} onValueChange={setFClient}>
          <SelectTrigger className="bg-black/40 border-white/10 text-slate-300 h-9 text-xs w-48" data-testid="inv-filter-client"><SelectValue placeholder="All Clients" /></SelectTrigger>
          <SelectContent className="bg-[#0f0f15] border-white/10 max-h-[250px] z-[200]">
            <SelectItem value="all">All Clients</SelectItem>
            {clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger className="bg-black/40 border-white/10 text-slate-300 h-9 text-xs w-44" data-testid="inv-filter-status"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="Pending">Pending</SelectItem>
            <SelectItem value="Partially Paid">Partially Paid</SelectItem>
            <SelectItem value="Cleared">Cleared</SelectItem>
            <SelectItem value="Overdue">Overdue</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button onClick={() => setShowCreate(true)} className="bg-indigo-600 hover:bg-indigo-700 shadow-[0_0_15px_rgba(79,70,229,0.3)] h-9 text-xs" data-testid="create-invoice-btn">
          <Plus size={14} className="mr-1" /> New Invoice
        </Button>
      </div>

      {/* Table */}
      <div className="glass-card overflow-hidden" data-testid="invoices-table">
        <div className="overflow-x-auto mobile-cards">
        <table className="data-table">
          <thead><tr>
            <th>Invoice</th><th>Client</th><th>Scope</th><th>Date</th><th>Total</th><th>Received</th><th>Status</th><th></th>
          </tr></thead>
          <tbody>
            {invoices.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-12 text-slate-500">No invoices found</td></tr>
            ) : invoices.map(inv => (
              <tr key={inv.invoice_id} className="cursor-pointer" onClick={() => setSelectedInvoice(inv)} data-testid={`inv-row-${inv.invoice_id}`}>
                <td className="text-white text-sm font-medium">{inv.name}</td>
                <td className="text-slate-300 text-sm">{inv.client_name}</td>
                <td><span className={`text-[10px] px-2 py-0.5 rounded border ${inv.scope_type === 'Retainer' ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' : 'bg-purple-500/10 text-purple-400 border-purple-500/20'}`}>{inv.scope_type}</span></td>
                <td className="text-xs text-slate-400">{inv.invoice_date}</td>
                <td className="text-sm font-mono text-white font-bold">{formatCurrency(inv.total_amount)}</td>
                <td className="text-sm font-mono text-emerald-400">{formatCurrency(inv.total_received)}</td>
                <td><span className={`text-[10px] px-2 py-0.5 rounded border ${STATUS_COLORS[inv.status]}`}>{inv.status}</span></td>
                <td>
                  <div className="flex items-center gap-1">
                    {isAdmin && <ChangeHistoryButton resource="invoices" recordId={inv.invoice_id} />}
                    <ChevronRight size={14} className="text-slate-600" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Dialogs */}
      {(showCreate || editInvoice) && (
        <InvoiceFormDialog
          invoice={editInvoice} clients={clients}
          onClose={() => { setShowCreate(false); setEditInvoice(null); }}
          onSaved={() => { setShowCreate(false); setEditInvoice(null); fetchData(); }}
        />
      )}
      {selectedInvoice && (
        <InvoiceDetailSheet
          invoice={selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
          onEdit={() => { setEditInvoice(selectedInvoice); setSelectedInvoice(null); }}
          onDelete={() => handleDelete(selectedInvoice.invoice_id)}
          onRecordPayment={() => { setShowPayment(selectedInvoice); setSelectedInvoice(null); }}
          onRefresh={fetchData}
        />
      )}
      {showPayment && (
        <PaymentDialog
          invoice={showPayment}
          onClose={() => setShowPayment(null)}
          onSaved={() => { setShowPayment(null); fetchData(); }}
        />
      )}
    </SidebarLayout>
  );
}

// ========== INVOICE FORM ==========
function InvoiceFormDialog({ invoice, clients, onClose, onSaved }) {
  const isEdit = !!invoice;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: invoice?.name || '',
    client_id: invoice?.client_id || '',
    scope_type: invoice?.scope_type || '',
    scope_name: invoice?.scope_name || '',
    invoice_date: invoice?.invoice_date || new Date().toISOString().split('T')[0],
    due_date: invoice?.due_date || '',
    amount: invoice?.amount ?? '',
    tax_amount: invoice?.tax_amount ?? '',
    notes: invoice?.notes || '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    setError(''); setLoading(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) || 0, tax_amount: parseFloat(form.tax_amount) || 0 };
      if (isEdit) {
        await axios.put(`${API}/invoices/${invoice.invoice_id}`, payload, { withCredentials: true });
        toast.success('Invoice updated');
      } else {
        await axios.post(`${API}/invoices`, payload, { withCredentials: true });
        toast.success('Invoice created');
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-[#0a0a0f] border-white/10 max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-white font-heading text-lg">{isEdit ? 'Edit Invoice' : 'New Invoice'}</DialogTitle></DialogHeader>
        {error && <div className="p-2 rounded bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs">{error}</div>}
        <div className="space-y-3 py-2">
          <div><label className="text-xs text-slate-500 mb-1 block">Invoice Name *</label>
            <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Jan 2026 Retainer" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="inv-name" /></div>
          <div><label className="text-xs text-slate-500 mb-1 block">Client *</label>
            <Select value={form.client_id} onValueChange={(v) => set('client_id', v)}>
              <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="inv-client"><SelectValue placeholder="Select client" /></SelectTrigger>
              <SelectContent className="bg-[#0f0f15] border-white/10 max-h-[250px] z-[200]">
                {clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-slate-500 mb-1 block">Scope Type *</label>
              <Select value={form.scope_type} onValueChange={(v) => set('scope_type', v)}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="inv-scope-type"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
                  {SCOPE_TYPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><label className="text-xs text-slate-500 mb-1 block">Scope Name</label>
              <Input value={form.scope_name} onChange={(e) => set('scope_name', e.target.value)} placeholder="e.g. Social Media Mgmt" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="inv-scope-name" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-slate-500 mb-1 block">Invoice Date *</label>
              <input type="date" value={form.invoice_date} onChange={(e) => set('invoice_date', e.target.value)} className="w-full bg-black/40 border border-white/10 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500" data-testid="inv-date" /></div>
            <div><label className="text-xs text-slate-500 mb-1 block">Due Date</label>
              <input type="date" value={form.due_date} onChange={(e) => set('due_date', e.target.value)} className="w-full bg-black/40 border border-white/10 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500" data-testid="inv-due-date" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-slate-500 mb-1 block">Amount (before tax) *</label>
              <Input type="number" value={form.amount} onChange={(e) => set('amount', e.target.value)} placeholder="0" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="inv-amount" /></div>
            <div><label className="text-xs text-slate-500 mb-1 block">Tax (GST)</label>
              <Input type="number" value={form.tax_amount} onChange={(e) => set('tax_amount', e.target.value)} placeholder="0" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="inv-tax" /></div>
          </div>
          {(form.amount || form.tax_amount) && (
            <div className="text-right text-xs text-slate-400">Total: <span className="text-white font-bold">{formatCurrency((parseFloat(form.amount) || 0) + (parseFloat(form.tax_amount) || 0))}</span></div>
          )}
          <div><label className="text-xs text-slate-500 mb-1 block">Notes</label>
            <Input value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Optional notes..." className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="inv-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-white/5 border-white/10 text-white">Cancel</Button>
          <Button disabled={loading || !form.name || !form.client_id || !form.scope_type || !form.amount} onClick={handleSubmit} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-invoice">{loading ? 'Saving...' : isEdit ? 'Update' : 'Create Invoice'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ========== PAYMENT DIALOG ==========
function PaymentDialog({ invoice, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const remaining = invoice.total_amount - invoice.total_received - (invoice.total_tax_deducted || 0);
  const [form, setForm] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    amount_received: '',
    payment_mode: '',
    tax_deduction: '',
    payment_type: '',
    reference_id: '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/invoices/${invoice.invoice_id}/payments`, {
        ...form,
        amount_received: parseFloat(form.amount_received) || 0,
        tax_deduction: parseFloat(form.tax_deduction) || 0,
      }, { withCredentials: true });
      toast.success('Payment recorded & income entry created');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-[#0a0a0f] border-white/10">
        <DialogHeader><DialogTitle className="text-white font-heading text-lg">Record Payment</DialogTitle></DialogHeader>
        <div className="glass-card p-3 mb-2">
          <div className="flex justify-between text-xs"><span className="text-slate-500">Invoice</span><span className="text-white">{invoice.name}</span></div>
          <div className="flex justify-between text-xs mt-1"><span className="text-slate-500">Total</span><span className="text-white font-bold">{formatCurrency(invoice.total_amount)}</span></div>
          <div className="flex justify-between text-xs mt-1"><span className="text-slate-500">Already Received</span><span className="text-emerald-400">{formatCurrency(invoice.total_received)}</span></div>
          <div className="flex justify-between text-xs mt-1"><span className="text-slate-500">Remaining</span><span className="text-amber-400 font-bold">{formatCurrency(remaining)}</span></div>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-slate-500 mb-1 block">Payment Date *</label>
              <input type="date" value={form.payment_date} onChange={(e) => set('payment_date', e.target.value)} className="w-full bg-black/40 border border-white/10 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500" data-testid="pay-date" /></div>
            <div><label className="text-xs text-slate-500 mb-1 block">Amount Received *</label>
              <Input type="number" value={form.amount_received} onChange={(e) => set('amount_received', e.target.value)} placeholder="0" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="pay-amount" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-slate-500 mb-1 block">Payment Type *</label>
              <Select value={form.payment_type} onValueChange={(v) => set('payment_type', v)}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="pay-type"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
                  <SelectItem value="Full Payment">Full Payment</SelectItem>
                  <SelectItem value="Part Payment">Part Payment</SelectItem>
                </SelectContent>
              </Select></div>
            <div><label className="text-xs text-slate-500 mb-1 block">Payment Mode *</label>
              <Select value={form.payment_mode} onValueChange={(v) => set('payment_mode', v)}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="pay-mode"><SelectValue placeholder="Mode" /></SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
                  {PAYMENT_MODES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-slate-500 mb-1 block">Tax Deduction (TDS)</label>
              <Input type="number" value={form.tax_deduction} onChange={(e) => set('tax_deduction', e.target.value)} placeholder="0" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="pay-tds" /></div>
            <div><label className="text-xs text-slate-500 mb-1 block">Reference / TXN ID</label>
              <Input value={form.reference_id} onChange={(e) => set('reference_id', e.target.value)} placeholder="Optional" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="pay-ref" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-white/5 border-white/10 text-white">Cancel</Button>
          <Button disabled={loading || !form.amount_received || !form.payment_type || !form.payment_mode} onClick={handleSubmit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-payment">
            {loading ? 'Recording...' : 'Record Payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ========== DETAIL SHEET ==========
function InvoiceDetailSheet({ invoice, onClose, onEdit, onDelete, onRecordPayment }) {
  const inv = invoice;
  return (
    <Sheet open={true} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg bg-[#0a0a0f] border-l border-white/10 overflow-y-auto">
        <SheetHeader className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] px-2 py-0.5 rounded border ${STATUS_COLORS[inv.status]}`}>{inv.status}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded border ${inv.scope_type === 'Retainer' ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' : 'bg-purple-500/10 text-purple-400 border-purple-500/20'}`}>{inv.scope_type}</span>
          </div>
          <SheetTitle className="text-white font-heading text-xl" data-testid="inv-detail-title">{inv.name}</SheetTitle>
        </SheetHeader>
        <div className="space-y-4">
          <div className="glass-card p-4">
            <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-3">Details</h3>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between"><span className="text-slate-500">Client</span><span className="text-white">{inv.client_name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Scope</span><span className="text-white">{inv.scope_name || '—'}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Invoice Date</span><span className="text-white">{inv.invoice_date}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Due Date</span><span className="text-white">{inv.due_date || '—'}</span></div>
            </div>
          </div>
          <div className="glass-card p-4">
            <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-3">Financials</h3>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between"><span className="text-slate-500">Amount</span><span className="text-white">{formatCurrency(inv.amount)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Tax (GST)</span><span className="text-white">{formatCurrency(inv.tax_amount)}</span></div>
              <div className="flex justify-between border-t border-white/5 pt-2"><span className="text-slate-400 font-semibold">Total</span><span className="text-white font-bold">{formatCurrency(inv.total_amount)}</span></div>
              <div className="flex justify-between"><span className="text-emerald-500">Received</span><span className="text-emerald-400 font-bold">{formatCurrency(inv.total_received)}</span></div>
              {inv.total_tax_deducted > 0 && <div className="flex justify-between"><span className="text-slate-500">TDS Deducted</span><span className="text-amber-400">{formatCurrency(inv.total_tax_deducted)}</span></div>}
              <div className="flex justify-between"><span className="text-amber-500">Outstanding</span><span className="text-amber-400 font-bold">{formatCurrency(inv.total_amount - inv.total_received - (inv.total_tax_deducted || 0))}</span></div>
            </div>
          </div>
          {(inv.payments || []).length > 0 && (
            <div className="glass-card p-4">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-3">Payment History</h3>
              <div className="space-y-2">
                {inv.payments.map((p, i) => (
                  <div key={i} className="flex items-start justify-between text-xs border-b border-white/5 pb-2 last:border-0">
                    <div>
                      <div className="text-white font-medium">{formatCurrency(p.amount_received)}</div>
                      <div className="text-slate-500">{p.payment_date} &middot; {p.payment_mode} &middot; {p.payment_type}</div>
                      {p.tax_deduction > 0 && <div className="text-amber-400 text-[10px]">TDS: {formatCurrency(p.tax_deduction)}</div>}
                    </div>
                    {p.reference_id && <span className="text-[10px] text-slate-500 font-mono">{p.reference_id}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {inv.notes && (
            <div className="glass-card p-4">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-2">Notes</h3>
              <p className="text-xs text-slate-300">{inv.notes}</p>
            </div>
          )}
          <div className="flex gap-2 flex-wrap">
            {inv.status !== 'Cleared' && (
              <Button onClick={onRecordPayment} className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-9 text-xs" data-testid="record-payment-btn">
                <CreditCard size={14} className="mr-1" /> Record Payment
              </Button>
            )}
            <Button onClick={onEdit} variant="outline" className="bg-white/5 border-white/10 text-white h-9 text-xs" data-testid="edit-invoice-btn">Edit</Button>
            <Button onClick={onDelete} variant="outline" className="bg-rose-500/10 border-rose-500/20 text-rose-400 h-9 text-xs" data-testid="delete-invoice-btn"><X size={14} /></Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
